import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Loader2, PenLine, MessageSquareText, XCircle, Settings2, CheckCircle2, Clock, Sparkles, BarChart3 } from 'lucide-react';
import { CollapsibleSection } from './CollapsibleSection';
import { NdaReviewPanel } from './NdaReviewPanel';

const DEADLINE_MS = 72 * 3600 * 1000;

export function CountdownBar({ ts, respondedLabel }) {
  const [, tick] = React.useReducer(x => x + 1, 0);
  useEffect(() => { const t = setInterval(tick, 60000); return () => clearInterval(t); }, []);
  if (respondedLabel) {
    return (
      <div className="flex items-center gap-1.5 mt-2.5 text-[11px] text-emerald-400" data-testid="countdown-responded">
        <CheckCircle2 className="w-3.5 h-3.5" /> {respondedLabel}
      </div>
    );
  }
  const elapsed = Date.now() - new Date(ts).getTime();
  const pct = Math.max(0, Math.min(100, (elapsed / DEADLINE_MS) * 100));
  const leftMs = Math.max(0, DEADLINE_MS - elapsed);
  const leftH = leftMs / 3600000;
  const label = leftMs <= 0 ? '72h-Deadline überschritten!'
    : leftH < 1 ? `${Math.round(leftH * 60)} Min bis zur 72h-Deadline`
    : `${Math.floor(leftH)}h bis zur 72h-Deadline`;
  const hot = pct >= 85;
  const color = hot ? 'bg-red-500' : pct >= 60 ? 'bg-amber-500' : 'bg-emerald-500';
  const textColor = hot ? 'text-red-400' : pct >= 60 ? 'text-amber-400' : 'text-muted-foreground';
  return (
    <div className="mt-2.5" data-testid="countdown-bar">
      <div className={`flex items-center gap-1.5 text-[11px] ${textColor} ${hot ? 'font-bold' : ''}`}>
        <Clock className={`w-3.5 h-3.5 ${hot ? 'animate-pulse' : ''}`} /> {label}
      </div>
      <div className="h-1.5 bg-secondary rounded-full mt-1 overflow-hidden">
        <div className={`h-full rounded-full transition-[width] ${color} ${hot ? 'animate-pulse' : ''}`}
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Anklickbare Absagegründe — abgeleitet aus den OLTA-Brokered-Kriterien (Investmentthese).
const DECLINE_REASONS = [
  { id: 'these', label: 'Kein Fit mit Investmentthese', text: 'Das Unternehmen passt nicht zu unserer Investmentthese — wir suchen infrastruktur-nahe B2B-Unternehmen (Energie, Telekommunikation, Transport, Versorger, öffentliche Hand) im deutschen Mittelstand.' },
  { id: 'ebitda_low', label: 'EBITDA zu gering (< 2 Mio. €)', text: 'Das Unternehmen liegt mit seinem EBITDA unterhalb unseres Zielkorridors von 2–5 Mio. EUR.' },
  { id: 'margin', label: 'EBITDA-Marge < 15 %', text: 'Die EBITDA-Marge liegt unterhalb unserer Zielmarke von über 15 %.' },
  { id: 'too_big', label: 'Zu groß (EV > 25 Mio. €)', text: 'Das Unternehmen liegt oberhalb unseres Zielkorridors von 5–25 Mio. EUR Enterprise Value.' },
  { id: 'sector', label: 'Falsche Branche', text: 'Das Unternehmen ist nicht im infrastruktur-nahen B2B-Umfeld tätig, auf das wir uns fokussieren.' },
  { id: 'region', label: 'Außerhalb Deutschlands', text: 'Wir fokussieren uns ausschließlich auf Unternehmen mit Sitz in Deutschland.' },
  { id: 'majority', label: 'Keine 100 % / Mehrheit', text: 'Wir streben idealerweise einen 100%-Erwerb im Rahmen einer Nachfolge- oder Sondersituation an, was hier nicht darstellbar ist.' },
  { id: 'recurring', label: 'Keine wiederkehrenden B2B-Umsätze', text: 'Das Geschäftsmodell weist nicht die von uns gesuchten wiederkehrenden B2B-Umsätze mit planbaren Margen auf.' },
  { id: 'history', label: 'Zu kurze profitable Historie', text: 'Wir suchen Unternehmen mit über zehn Jahren profitabler operativer Historie.' },
  { id: 'asset_owner', label: 'Asset-Eigentümer statt Dienstleister', text: 'Wir erwerben keine Infrastruktur-Asset-Eigentümer selbst, sondern B2B-Produkte und -Dienstleistungen entlang dieser Sektoren.' },
];

export function DeclineDialog({ proposal, open, onClose, onDone, onPassInternal }) {
  const [reason, setReason] = useState('');
  const [selReasons, setSelReasons] = useState([]);
  const [emailBody, setEmailBody] = useState('');
  const emailTouched = React.useRef(false);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [aiInfo, setAiInfo] = useState(null);
  const suggest = useCallback(async (pid) => {
    setSuggesting(true);
    setSelReasons([]);
    try {
      const r = await api.suggestDeclineReason(pid);
      setReason(r.data.reason || '');
      // Talu, 04.11.2026: the AI writes the whole mail (teaser + e-mail + Prio-A sectors) — it replaces the template text
      if (r.data.email_body) {
        emailTouched.current = true;
        setEmailBody(r.data.email_body);
        setAiInfo({ language: r.data.language, ...(r.data.sources || {}) });
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'KI-Vorschlag fehlgeschlagen'); }
    setSuggesting(false);
  }, []);
  const toggleReason = (id) => {
    setSelReasons(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      setReason(DECLINE_REASONS.filter(r => next.includes(r.id)).map(r => r.text).join(' '));
      return next;
    });
  };
  useEffect(() => {
    if (open && proposal) { setReason(''); setSelReasons([]); setEmailBody(''); setAiInfo(null); emailTouched.current = false; }
  }, [open, proposal]);
  // Keep the editable e-mail preview in sync with the reason — until the user edits the text himself.
  useEffect(() => {
    if (!open || !proposal || emailTouched.current) return undefined;
    const t = setTimeout(() => {
      api.getProposalEmailPreview(proposal.id, 'decline', { reason: reason || '{grund}' })
        .then(r => { if (!emailTouched.current) setEmailBody(r.data.body || ''); })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [open, proposal, reason]);
  const resetEmail = () => {
    emailTouched.current = false;
    setAiInfo(null);
    api.getProposalEmailPreview(proposal.id, 'decline', { reason: reason || '{grund}' })
      .then(r => setEmailBody(r.data.body || ''))
      .catch(() => {});
  };
  const send = async () => {
    if (reason.trim().length < 3) { toast.error('Bitte einen Grund eintragen'); return; }
    setBusy(true);
    try {
      await api.declineProposal(proposal.id, reason.trim(), emailTouched.current ? emailBody : '');
      toast.success('Absage gesendet — Antwort ging im selben E-Mail-Thread raus');
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Absage fehlgeschlagen'); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="decline-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Absagen · {proposal?.ai?.company_name || proposal?.from_name || ''}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">
          „KI-Absage schreiben“ liest den Teaser-Anhang und die ganze Broker-Mail und formuliert die komplette Absage — mit konkretem
          Grund, einem Absatz zu dem, was wir gerade suchen (Prio-A-Branchen + Fokus-Text aus den Broker-Vorlagen), in der Sprache des
          Brokers. Du kannst alles unten anpassen — gesendet wird als Antwort an {proposal?.from_email}.
        </p>
        <div>
          <p className="text-[10px] text-foreground/50 uppercase tracking-wider mb-1.5">Interner Absagegrund (Akte) — anklicken (mehrere möglich) oder frei formulieren</p>
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="decline-reason-chips">
            {DECLINE_REASONS.map(r => (
              <button key={r.id} onClick={() => toggleReason(r.id)} disabled={suggesting || busy}
                data-testid={`decline-reason-chip-${r.id}`} title={r.text}
                className={`text-[11px] px-2.5 py-1.5 rounded-sm border transition-colors disabled:opacity-50 ${
                  selReasons.includes(r.id)
                    ? 'border-red-500/60 bg-red-500/15 text-red-200'
                    : 'border-border text-foreground/70 hover:border-red-500/40 hover:text-red-300'
                }`}>
                {r.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
              placeholder={suggesting ? 'KI liest Teaser und E-Mail und schreibt die Absage…' : 'Grund oben anklicken oder hier frei formulieren…'}
              data-testid="decline-reason-input" className="text-sm" disabled={suggesting} />
            {suggesting && (
              <div className="absolute inset-0 flex items-center justify-center gap-2 text-xs text-sky-300 bg-background/60 rounded-md" data-testid="decline-suggest-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> KI liest Teaser + E-Mail und schreibt die Absage (bis zu 1 Min.)…
              </div>
            )}
          </div>
          <button onClick={() => suggest(proposal.id)} disabled={suggesting || busy} data-testid="decline-suggest-btn"
            className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 mt-1.5 rounded-sm border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 transition-colors disabled:opacity-50">
            <Sparkles className="w-3.5 h-3.5" /> KI-Absage schreiben (Teaser + E-Mail + Prio-A-Fokus)
          </button>
          {aiInfo && (
            <p className="text-[10px] text-emerald-300/90 mt-1.5" data-testid="decline-ai-sources">
              KI-Mail erstellt · {aiInfo.teaser ? `Teaser gelesen (${aiInfo.teaser_source || 'Anhang'})` : 'kein Teaser-Anhang lesbar — nur E-Mail'}
              {' · '}{aiInfo.language === 'en' ? 'Englisch' : 'Deutsch'}
              {aiInfo.prio_a_sectors?.length ? ` · Prio-A-Branchen: ${aiInfo.prio_a_sectors.slice(0, 4).join(', ')}` : ' · keine Prio-A-Deals, nur Fokus-Text'}
            </p>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] text-foreground/50 uppercase tracking-wider">E-Mail-Text (editierbar)</p>
            {emailTouched.current && (
              <button onClick={resetEmail} data-testid="decline-email-reset-btn"
                className="text-[10px] text-muted-foreground hover:text-sky-300 underline">Auf Vorlage zurücksetzen</button>
            )}
          </div>
          <Textarea value={emailBody}
            onChange={e => { emailTouched.current = true; setEmailBody(e.target.value); }}
            rows={aiInfo ? 16 : 9} data-testid="decline-email-input" className="text-sm font-mono" />
          <p className="text-[10px] text-muted-foreground mt-1">Die OLTA-Signatur wird beim Versand automatisch angehängt.</p>
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {onPassInternal ? (
            <button onClick={onPassInternal} disabled={busy || suggesting} data-testid="decline-pass-internal-btn"
              title="Deal intern passen, ohne dem Broker eine Absage zu schicken — z. B. wenn ihm bereits anderweitig abgesagt wurde."
              className="text-[11px] px-2.5 py-2 rounded-sm border border-border text-foreground/70 hover:text-foreground hover:border-foreground/40 transition-colors disabled:opacity-50">
              Nur intern passen (keine E-Mail an den Broker)
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm text-xs h-9">Abbrechen</Button>
            <Button onClick={send} disabled={busy || suggesting} data-testid="decline-send-btn"
              className="bg-red-600 hover:bg-red-500 text-white rounded-sm text-xs h-9">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <XCircle className="w-4 h-4 mr-1.5" />}
              Absage senden
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function NdaRequestDialog({ proposal, open, onClose, onDone }) {
  const [emailBody, setEmailBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !proposal) return;
    setLoading(true);
    api.getProposalEmailPreview(proposal.id, 'nda_request')
      .then(r => setEmailBody(r.data.body || ''))
      .catch(() => toast.error('Vorlage konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [open, proposal]);
  const send = async () => {
    if (emailBody.trim().length < 10) { toast.error('Bitte E-Mail-Text prüfen'); return; }
    setBusy(true);
    try {
      await api.requestProposalNda(proposal.id, emailBody);
      toast.success('NDA-Anforderung gesendet ✓');
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="nda-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">NDA anfordern · {proposal?.from_name || proposal?.from_email || ''}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">
          Die E-Mail wird als Antwort im selben Thread an {proposal?.from_email} gesendet — Text vor dem Versand frei anpassbar.
        </p>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <Textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={10}
              data-testid="nda-request-email-input" className="text-sm font-mono" />
            <p className="text-[10px] text-muted-foreground -mt-1">Die OLTA-Signatur wird beim Versand automatisch angehängt.</p>
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm text-xs h-9">Abbrechen</Button>
          <Button onClick={send} disabled={busy || loading} data-testid="nda-request-send-btn"
            className="bg-violet-600 hover:bg-violet-500 text-white rounded-sm text-xs h-9">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MessageSquareText className="w-4 h-4 mr-1.5" />}
            NDA anfordern
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SignaturePad({ onChange }) {
  const canvasRef = React.useRef(null);
  const drawing = React.useRef(false);
  const hasInk = React.useRef(false);
  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const down = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.strokeStyle = '#1a2560';
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    canvasRef.current.setPointerCapture(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    hasInk.current = true;
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk.current) onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
    onChange(null);
  };
  return (
    <div className="space-y-1.5">
      <canvas ref={canvasRef} width={620} height={170}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        data-testid="nda-signature-canvas"
        className="w-full bg-white rounded-sm border border-border cursor-crosshair touch-none"
        style={{ height: 130 }} />
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-muted-foreground">Mit Maus, Finger oder Stift direkt auf dem Bildschirm unterschreiben.</p>
        <button onClick={clear} data-testid="nda-signature-clear"
          className="text-[11px] text-muted-foreground hover:text-red-400 underline">Löschen</button>
      </div>
    </div>
  );
}

export function NdaDialog({ proposal, open, onClose, onDone, userName }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [sigName, setSigName] = useState(userName || '');
  const [sigMode, setSigMode] = useState('draw');
  const [sigData, setSigData] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [signEmail, setSignEmail] = useState('');
  const signEmailTouched = React.useRef(false);
  const [remarksEmail, setRemarksEmail] = useState('');
  const remarksEmailTouched = React.useRef(false);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    if (!open || !proposal) return;
    setSigName(userName || '');
    setRemarks('');
    setSigData(null);
    setSigMode('draw');
    setSignEmail('');
    signEmailTouched.current = false;
    setRemarksEmail('');
    remarksEmailTouched.current = false;
    api.getProposalEmailPreview(proposal.id, 'nda_signed')
      .then(r => { if (!signEmailTouched.current) setSignEmail(r.data.body || ''); })
      .catch(() => {});
    let url;
    api.getProposalNdaFile(proposal.id)
      .then(r => { url = URL.createObjectURL(r.data); setPdfUrl(url); })
      .catch(() => toast.error('NDA-PDF konnte nicht geladen werden'));
    return () => { if (url) URL.revokeObjectURL(url); setPdfUrl(null); };
  }, [open, proposal, userName]);
  useEffect(() => {
    if (!open || !proposal || !remarks.trim() || remarksEmailTouched.current) return;
    const t = setTimeout(() => {
      api.getProposalEmailPreview(proposal.id, 'nda_remarks', { remarks: remarks.trim() })
        .then(r => { if (!remarksEmailTouched.current) setRemarksEmail(r.data.body || ''); })
        .catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [remarks, open, proposal]);
  const act = async (mode) => {
    if (mode === 'sign') {
      if (sigMode === 'draw' && !sigData) { toast.error('Bitte erst auf dem Bildschirm unterschreiben (oder auf „Tippen" wechseln)'); return; }
      if (sigMode === 'type' && sigName.trim().length < 3) { toast.error('Bitte vollständigen Namen eintragen'); return; }
    }
    if (mode === 'remarks' && remarks.trim().length < 5) { toast.error('Bitte Anmerkungen eintragen'); return; }
    if (mode === 'mark_signed' && !window.confirm('Das NDA als vom Broker unterschrieben markieren? Es wird nichts versendet — der Deal wechselt auf „NDA gezeichnet · wartet auf Unterlagen".')) return;
    setBusy(mode);
    try {
      const r = await api.countersignProposalNda(proposal.id, {
        mode,
        signature_name: sigName.trim(),
        signature_png_b64: (mode === 'sign' && sigMode === 'draw' && sigData) ? sigData.split(',')[1] : '',
        remarks: remarks.trim(),
        email_body: (mode === 'sign' && signEmailTouched.current) ? signEmail
          : (mode === 'remarks' && remarksEmailTouched.current) ? remarksEmail : '',
      });
      toast.success(mode === 'sign'
        ? (r.data.mode === 'approval_requested'
          ? 'Zur Freigabe ans Team gesendet — nach dem Approval geht das signierte NDA automatisch an den Broker (Vier-Augen-Prinzip)'
          : 'NDA gegengezeichnet und an den Broker zurückgesendet ✓')
        : mode === 'mark_signed'
          ? 'Als unterschrieben markiert — der Deal wartet jetzt auf die Unterlagen ✓'
          : 'Anmerkungen an den Broker gesendet ✓');
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(null);
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="nda-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading">NDA · {proposal?.nda_attachment?.name || ''}</DialogTitle>
        </DialogHeader>
        {proposal?.nda_signed_at ? (
          <p className="text-xs text-emerald-400">Bereits gegengezeichnet von {proposal.nda_signed_by} und zurückgesendet.</p>
        ) : proposal?.nda_sign_request ? (
          <p className="text-xs text-sky-300" data-testid="nda-pending-approval-note">
            {`Wartet auf Team-Freigabe (Vier-Augen-Prinzip) — angestoßen von ${proposal.nda_sign_request.requested_by}. Nach dem Approval in „My Pending Reviews“ geht das signierte NDA automatisch an den Broker.`}
          </p>
        ) : proposal?.nda_sign_rejection ? (
          <p className="text-xs text-amber-400" data-testid="nda-rejected-note">
            {`Freigabe abgelehnt von ${proposal.nda_sign_rejection.by}: „${proposal.nda_sign_rejection.reason}“ — du kannst das NDA anpassen lassen oder neu zur Freigabe geben.`}
          </p>
        ) : null}
        <div className="border border-border rounded-sm overflow-hidden bg-white" style={{ height: '38vh' }}>
          {pdfUrl
            ? <iframe src={pdfUrl} title="NDA" className="w-full h-full" data-testid="nda-pdf-frame" />
            : <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}
        </div>
        {!proposal?.nda_signed_at && !proposal?.nda_sign_request && (
          <div className="space-y-3">
            <NdaReviewPanel proposal={proposal}
              onApplyComments={(t) => { setRemarks(t); remarksEmailTouched.current = false; }} />
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Unterschrift:</span>
              <div className="flex rounded-sm border border-border overflow-hidden">
                <button onClick={() => { setSigMode('draw'); setSigData(null); }} data-testid="nda-sig-mode-draw"
                  className={`px-3 py-1 text-[11px] transition-colors ${sigMode === 'draw' ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}>
                  Zeichnen
                </button>
                <button onClick={() => setSigMode('type')} data-testid="nda-sig-mode-type"
                  className={`px-3 py-1 text-[11px] transition-colors ${sigMode === 'type' ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}>
                  Tippen
                </button>
              </div>
            </div>
            {sigMode === 'draw' ? (
              <SignaturePad onChange={setSigData} />
            ) : (
              <div className="space-y-1.5">
                <Input value={sigName} onChange={e => setSigName(e.target.value)} className="text-sm h-9"
                  data-testid="nda-signature-input" placeholder="Vor- und Nachname" />
                <p className="text-[10px] text-muted-foreground">
                  Wird in Schreibschrift aufs PDF gesetzt{sigName.trim() ? ': ' : '.'}
                  {sigName.trim() && <span style={{ fontFamily: 'cursive', fontSize: 15 }} className="text-foreground">{sigName}</span>}
                </p>
              </div>
            )}
            <Textarea value={remarks} onChange={e => { setRemarks(e.target.value); remarksEmailTouched.current = false; }} rows={3}
              placeholder={'Optionale Anmerkungen zum NDA — für „Nur Anmerkungen senden" erforderlich.'}
              data-testid="nda-remarks-input" className="text-sm" />
            {remarks.trim() && (
              <div>
                <p className="text-[10px] text-foreground/50 uppercase tracking-wider mb-1">Anmerkungs-E-Mail an den Broker (Vorschau, editierbar)</p>
                <Textarea value={remarksEmail}
                  onChange={e => { remarksEmailTouched.current = true; setRemarksEmail(e.target.value); }}
                  rows={7} data-testid="nda-remarks-email-input" className="text-sm font-mono" />
                <p className="text-[10px] text-muted-foreground mt-1">Die OLTA-Signatur wird beim Versand automatisch angehängt.</p>
              </div>
            )}
            <div>
              <p className="text-[10px] text-foreground/50 uppercase tracking-wider mb-1">Begleit-E-Mail beim Zurücksenden (editierbar)</p>
              <Textarea value={signEmail}
                onChange={e => { signEmailTouched.current = true; setSignEmail(e.target.value); }}
                rows={5} data-testid="nda-sign-email-input" className="text-sm font-mono" />
              <p className="text-[10px] text-muted-foreground mt-1">Die OLTA-Signatur wird beim Versand automatisch angehängt.</p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={onClose} className="rounded-sm text-xs h-9">Abbrechen</Button>
              <Button variant="outline" onClick={() => act('mark_signed')} disabled={!!busy} data-testid="nda-mark-signed-btn"
                title="Der Broker hat UNSER NDA unterschrieben zurückgeschickt — kein Gegenzeichnen nötig, nur Status setzen"
                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-sm text-xs h-9">
                {busy === 'mark_signed' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                Kam unterschrieben zurück
              </Button>
              <Button variant="outline" onClick={() => act('remarks')} disabled={!!busy} data-testid="nda-remarks-btn"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-sm text-xs h-9">
                {busy === 'remarks' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MessageSquareText className="w-4 h-4 mr-1.5" />}
                Nur Anmerkungen senden
              </Button>
              <Button onClick={() => act('sign')} disabled={!!busy} data-testid="nda-sign-btn"
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-9">
                {busy === 'sign' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <PenLine className="w-4 h-4 mr-1.5" />}
                Unterschreiben &amp; zurücksenden
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TemplatesDialog({ open, onClose }) {
  const [templates, setTemplates] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [saving, setSaving] = useState(null);
  const load = useCallback(() => {
    api.getBrokerTemplates()
      .then(r => { setTemplates(r.data.templates); setPlaceholders(r.data.placeholders || []); })
      .catch(() => toast.error('Vorlagen konnten nicht geladen werden'));
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const save = async (t) => {
    setSaving(t.id);
    try { await api.updateBrokerTemplate(t.id, t.body); toast.success(`Vorlage „${t.label}" gespeichert`); }
    catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(null);
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="templates-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">E-Mail-Vorlagen (Broker Inbox)</DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-muted-foreground -mt-1">
          Platzhalter: {placeholders.map(p => p.key).join(' · ')} — werden beim Versand automatisch ersetzt.
          Die OLTA-Signatur wird immer angehängt.
        </p>
        {!templates ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : templates.map((t, i) => (
          <div key={t.id} className="border border-border rounded-sm p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm font-medium text-foreground">{t.label}</p>
              <Button size="sm" onClick={() => save(t)} disabled={saving === t.id} data-testid={`template-save-${t.id}`}
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-7 px-3">
                {saving === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Speichern'}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">{t.hint}</p>
            <Textarea value={t.body} rows={6} data-testid={`template-body-${t.id}`}
              onChange={e => setTemplates(prev => prev.map((x, j) => j === i ? { ...x, body: e.target.value } : x))}
              className="text-xs font-mono" />
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

export function TemplatesButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="broker-templates-btn"
        className="border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-8">
        <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Vorlagen
      </Button>
      <TemplatesDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

const fitColor = (r) => r === null || r === undefined ? 'text-muted-foreground'
  : r >= 50 ? 'text-emerald-400' : r >= 25 ? 'text-amber-400' : 'text-red-400';

export function BrokerStatsSection() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.getBrokerInboxStats().then(r => setStats(r.data)).catch(() => setStats({ error: true }));
  }, []);
  const cats = stats?.decline_categories || [];
  const maxCat = Math.max(1, ...cats.map(c => c.count));
  const brokers = (stats?.brokers || []).filter(b => b.total > 0);
  return (
    <CollapsibleSection storageKey="dealinbox.stats" icon={BarChart3} iconClass="text-sky-400"
      title="Absage-Statistik & Broker-Qualität" defaultOpen={false} testId="broker-stats-section"
      count={stats?.totals ? `${stats.totals.declined || 0} Absagen · ${brokers.length} Broker` : undefined}
      bodyClass="p-4">
      {!stats ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : stats.error ? (
        <p className="text-xs text-red-400">Statistik konnte nicht geladen werden.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div data-testid="decline-categories">
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold mb-3">Häufigste Absagegründe</p>
            {cats.length === 0 ? (
              <p className="text-xs text-muted-foreground">Noch keine Absagen erfasst.</p>
            ) : cats.map((c, i) => (
              <div key={i} className="mb-2.5" title={(c.examples || []).join('\n')}>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground/85">{c.category}</span>
                  <span className="font-mono text-muted-foreground">{c.count}×</span>
                </div>
                <div className="h-1.5 bg-secondary rounded-full mt-1 overflow-hidden">
                  <div className="h-full bg-sky-500 rounded-full" style={{ width: `${(c.count / maxCat) * 100}%` }} />
                </div>
                {c.examples?.[0] && (
                  <p className="text-[10px] text-muted-foreground/60 italic mt-0.5 truncate">„{c.examples[0]}"</p>
                )}
              </div>
            ))}
          </div>
          <div data-testid="broker-quality-table">
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold mb-3">Broker-Qualität (Fit-Quote = angelegte Deals / bearbeitete)</p>
            {brokers.length === 0 ? (
              <p className="text-xs text-muted-foreground">Noch keine Broker-Deals erfasst.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] text-muted-foreground uppercase text-left">
                      <th className="pb-1.5 pr-2 font-medium">Broker</th>
                      <th className="pb-1.5 px-2 text-right font-medium">Deals</th>
                      <th className="pb-1.5 px-2 text-right font-medium text-emerald-400/80">Angelegt</th>
                      <th className="pb-1.5 px-2 text-right font-medium text-red-400/80">Abgesagt</th>
                      <th className="pb-1.5 pl-2 text-right font-medium">Fit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brokers.slice(0, 15).map((b, i) => (
                      <tr key={i} className="border-t border-border/50" data-testid={`broker-row-${i}`}>
                        <td className="py-1.5 pr-2">
                          <span className="text-foreground/85">{b.name || b.email}</span>
                          {b.name && <span className="text-muted-foreground/60 ml-1.5 hidden sm:inline">{b.email}</span>}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono">{b.total}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-emerald-400">{b.created}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-red-400">{b.declined + b.dismissed}</td>
                        <td className={`py-1.5 pl-2 text-right font-mono font-bold ${fitColor(b.fit_rate)}`}>
                          {b.fit_rate === null ? '–' : `${b.fit_rate}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
}
